import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Box, Typography, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { getGiftCertificateSuggestions, GiftCertificateSuggestionResponse } from 'src/shared/services/fundraiserService';

type FundraiserSuggestionsModalProps = {
  open: boolean;
  onSelect: (file: File) => void;
  onClose: () => void;
};

export const FundraiserSuggestionsModal = ({ open, onSelect, onClose }: FundraiserSuggestionsModalProps) => {
  const { t } = useTranslation(['fundraiser', 'common']);
  const [suggestions, setSuggestions] = useState<GiftCertificateSuggestionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selecting, setSelecting] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getGiftCertificateSuggestions()
      .then((res) => setSuggestions(res.data))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSelect = (suggestion: GiftCertificateSuggestionResponse) => {
    setSelecting(suggestion.fileName);

    // Konvertera base64 till File
    const byteString = atob(suggestion.base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i += 1) {
      bytes[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'image/png' });
    const file = new File([blob], suggestion.fileName, { type: 'image/png' });

    onSelect(file);
  };

  return (
    <Dialog open={open} maxWidth='md' fullWidth onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <AutoAwesomeOutlinedIcon fontSize='small' sx={{ mr: 1 }} />
        {t('fundraiser:giftCertificate.suggestionsTitle')}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && suggestions.length === 0 && <Typography color='text.secondary'>{t('fundraiser:giftCertificate.suggestionsEmpty')}</Typography>}
        {!loading && suggestions.length > 0 && (
          <Grid container spacing={2}>
            {suggestions.map((s) => (
              <Grid item xs={6} sm={4} md={3} key={s.fileName}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: selecting ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': selecting ? {} : { borderColor: 'primary.main', boxShadow: 2 },
                    opacity: selecting && selecting !== s.fileName ? 0.5 : 1,
                  }}
                  onClick={() => !selecting && handleSelect(s)}
                >
                  <Box
                    component='img'
                    src={`data:image/png;base64,${s.base64}`}
                    alt={s.fileName}
                    sx={{ width: '100%', aspectRatio: '1 / 1.414', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={!!selecting}>
          {t('common:button.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
